import { Badge } from "@/components/ui/baseUi/badge"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { ProgramSubscriberDetailsLayout } from "@modules/programs/programSubscriber/components/programSubscriberDetails/programSubscriberDetailsLayout"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import dayjs from "dayjs"
import { ChevronRight, ClockIcon } from "lucide-react"
import { memo, Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

type ProgramSubscriberItemProps = {
  program: UserProgramDetailsEntity | ProgramBundleEntity
  subscriber: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  canSeeSubscriberDetails: boolean
} & React.ComponentProps<"div">

export const ProgramSubscriberItem: React.FC<ProgramSubscriberItemProps> = memo(
  ({ program, subscriber, canSeeSubscriberDetails, ...props }) => {
    const { sidebarNavigateTo, setCurrentStackState } = useSidebar()

    const openSubscriberDetails = () => {
      const sidebarContent = document.querySelector('[data-slot="right-sidebar-content"]')
      if (sidebarContent) {
        setCurrentStackState("subscribersListScrollPosition", sidebarContent.scrollTop.toString())
      }

      if (
        program instanceof ProgramBundleEntity &&
        subscriber instanceof ProgramBundleSubscriberEntity &&
        !subscriber.programBundleSubscriptionId
      ) {
        toast.error(word("program.subscribers.toast.invalidProgramOrSubscriberType.error"), {
          classNames: errorToastClassName(),
        })
        throw new Error("Invalid program or subscriber type")
      }

      void sidebarNavigateTo({
        content: (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <ProgramSubscriberDetailsLayout
                subscriberId={
                  subscriber instanceof ProgramBundleSubscriberEntity
                    ? subscriber.programBundleSubscriptionId
                    : subscriber.id
                      ? subscriber.id
                      : subscriber.box
                        ? subscriber.box.id
                        : subscriber.subscriptionId
                }
                isBoxSubscriber={subscriber instanceof ProgramSubscriberEntity && !!subscriber.box}
                program={program}
              />
            </Suspense>
          </ErrorBoundary>
        ),
        onHide: () => {
          return Promise.resolve(true)
        },
      })
    }

    return (
      <div
        key={subscriber.id}
        className={cn(
          "group mb-3 flex flex-row items-center justify-between",
          canSeeSubscriberDetails ? "cursor-pointer" : "",
        )}
        onClick={canSeeSubscriberDetails ? openSubscriberDetails : undefined}
        id={"subscriber" + subscriber.id}
        {...props}
      >
        <div className="flex w-9/10 flex-row items-center overflow-hidden">
          <UserCell
            avatarSize="size-12"
            className="w-full"
            member={{
              avatar: subscriber.avatar
                ? subscriber.avatar
                : subscriber instanceof ProgramSubscriberEntity && subscriber.box
                  ? subscriber.box.logo
                  : null,
              firstname: subscriber.firstname
                ? subscriber.firstname
                : subscriber instanceof ProgramSubscriberEntity && subscriber.box
                  ? subscriber.box.name
                  : "",
              lastname: subscriber.lastname ? subscriber.lastname : "",
            }}
            subtitle={
              subscriber.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ? (
                <Badge variant="neutralSubtle" size="md">
                  <ClockIcon />
                  {word("program.subscribers.tag.pending_payment")}
                </Badge>
              ) : subscriber.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ? (
                <Badge variant="negativeSubtle" size="md">
                  <WarningIcon color="currentColor" className="text-textError" />
                  {word("program.subscribers.tag.pending_cancellation", {
                    endDate: dayjs(subscriber.periodEndDate).diff(dayjs(), "days"),
                  })}
                </Badge>
              ) : subscriber.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION ? (
                <Badge variant="negativeSubtle" size="md">
                  <WarningIcon color="currentColor" className="text-textError" />
                  {word("program.subscribers.tag.pending_cancellation", {
                    endDate: dayjs(subscriber.trial?.endDate).diff(dayjs(), "days"),
                  })}
                </Badge>
              ) : subscriber.username ? (
                <Text fontStyle="BS" className="truncate text-textDefault-caption">
                  {`@${subscriber.username}`}
                </Text>
              ) : null
            }
            subtitleColor="neutral"
          />
        </div>

        {canSeeSubscriberDetails && (
          <div className="flex justify-end">
            <ChevronRight size={20} className="mr-3 transition-transform duration-200 group-hover:translate-x-2" />
          </div>
        )}
      </div>
    )
  },
)
